import { blackOrange, white, orange } from '@/theme/colors'
import { useCheckQrToken } from '@/hooks/useCheckQrToken'
import { useGenerateAuthQrToken } from '@/hooks/useGenerateAuthQrToken'
import { useAuth } from '@context/AuthContext'
import { queryKeys } from '@lib/queryKeys'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import GoogleLoginButton from '../Auth/GoogleLoginButton'
import { config } from '../../config'
import {
	Divider,
	Foot,
	GoogleSlot,
	Panel,
	PanelTitle,
	QrCodeImage,
	QrRow,
	QrText,
	QrTile,
} from './SignInPanel.styles'
import Spinner from '@components/Spinner'

interface SignInPanelProps {
	title: string
	/** Extra content below the QR row. */
	children?: React.ReactNode
	/** The "Download the Mobile App" foot; off where the page has its own app link. */
	showAppFoot?: boolean
}

/**
 * The one sign-in surface: Google, or scan the QR from a phone that is already
 * signed in. Owns the QR token lifecycle end to end (generate, poll, and set
 * the session on a scan) so every page that offers sign-in behaves identically.
 */
const SignInPanel: React.FC<SignInPanelProps> = ({ title, children, showAppFoot = true }) => {
	const { setAuth } = useAuth()
	const queryClient = useQueryClient()
	const { data: authQrTokenData, isLoading } = useGenerateAuthQrToken()
	const { data: qrCheckData } = useCheckQrToken(authQrTokenData?.token, !isLoading)

	useEffect(() => {
		if (qrCheckData?.user) {
			// User has authenticated via QR code
			setAuth({ token: qrCheckData.token, user: qrCheckData.user })
			// Reset the query data to prevent auto-login after logout
			queryClient.resetQueries({ queryKey: queryKeys.qrTokenCheck(authQrTokenData?.token) })
		}
	}, [qrCheckData, authQrTokenData?.token, setAuth, queryClient])

	return (
		<Panel>
			<PanelTitle>{title}</PanelTitle>

			<GoogleSlot>
				<GoogleLoginButton />
			</GoogleSlot>

			<Divider>or scan to sign in</Divider>

			<QrRow>
				<QrTile>
					{isLoading || !authQrTokenData?.token ? (
						<Spinner size={36} trackColor={`${orange}29`} />
					) : (
						<QrCodeImage
							size={92}
							value={`${config.getOrbitUrl}/appLink/qr-auth/${authQrTokenData.token}`}
							viewBox={`0 0 92 92`}
							fgColor={blackOrange}
							bgColor={white}
							level="M"
						/>
					)}
				</QrTile>
				<QrText>
					<b>Already on your phone?</b>
					<p>Open your camera, scan this code, and you&rsquo;re in instantly.</p>
				</QrText>
			</QrRow>

			{children}

			{showAppFoot && (
				<Foot>
					<a href={config.getOrbitUrl} target="_blank" rel="noreferrer">
						Download the Mobile App
					</a>
				</Foot>
			)}
		</Panel>
	)
}

export default SignInPanel
